import { Breadcrumb, Modal, Paginator, Table, TitleScreen, Tooltip } from "components/Core"
import SearchBarFilter from "components/SearchBarFilter"
import { get_data_table_reserves } from "helpers/data_tables"
import usePaginationTable from "hooks/usePaginationTable"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import { useEffect, useState } from "react"
import { HiPlus, HiViewGrid, HiViewList, HiOutlineEye } from "react-icons/hi"
import useFiltersTable from "hooks/useFiltersTable"
import { parsed_filters_on_search } from "helpers/data_tables"
import cn from "classnames"
import { format_date } from "helpers/datetime_helper"

const _states = {
  CLOSED: "Cerrada",
  OPEN: "Abierta",
  CANCELED: "Cancelada",
}

const ListScreen = () => {
  const [viewMode, setViewMode] = useState<"TABLE" | "BLOCK">("TABLE")
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const { loading_app, data, search_osrm_action } = useSearchMiddlewareOSRM({ entity: "reserves" })
  const { current_data, current_page, handle_page_change, handle_per_page, per_page, total_records, total_pages } =
    usePaginationTable({
      data,
    })

  const { filters_table } = useFiltersTable({ table_name: "reserves", onFilterApplied: () => handle_page_change(0) })

  useEffect(() => {
    search_osrm_action({
      from: per_page * current_page,
      size: per_page,
      filters: parsed_filters_on_search(filters_table.state),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current_page, per_page, filters_table.state])

  const [columns, data_table_items] = get_data_table_reserves(current_data)

  const handlePreview = (item: any) => {
    setSelectedItem(item)
    setIsPreviewOpen(true)
  }

  const DataGroup = ({
    title,
    children,
    className = "",
  }: {
    title: string
    children: React.ReactNode
    className?: string
  }) => (
    <div className={cn("w-full flex flex-col gap-1 mb-4", className)}>
      <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 mb-1">{title}</h3>
      <div className="w-full bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        <div className="flex flex-col">{children}</div>
      </div>
    </div>
  )

  const DataItem = ({ label, value, last }: { label: string; value: any; last?: boolean }) => {
    const isEmpty =
      value === null ||
      value === undefined ||
      value === "" ||
      (typeof value === "object" && Object.keys(value).length === 0)
    return (
      <div
        className={`flex flex-row justify-between items-center py-3 px-4 ${!last ? "border-b border-gray-100" : ""}`}
      >
        <p className="text-[13px] font-medium text-gray-500 uppercase tracking-tight">{label}</p>
        <p
          className={`text-[14px] font-bold text-right max-w-[60%] break-all ${isEmpty ? "text-gray-300 italic" : "text-gray-700"}`}
        >
          {isEmpty ? "[Vacío]" : typeof value === "object" ? JSON.stringify(value) : String(value)}
        </p>
      </div>
    )
  }

  const ItemPreviewModal = () => (
    <Modal
      mounted={isPreviewOpen}
      className="!w-[550px] !max-w-[95vw] shadow-2xl border border-gray-100 overflow-hidden"
    >
      <div className="w-full bg-[#F2F2F7] flex flex-col h-[85vh] max-h-[800px]">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-white/50 backdrop-blur-md">
          <div className="flex flex-row justify-between items-start">
            <div className="flex flex-col">
              <span className="text-[12px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                Vista Previa de Reserva
              </span>
              <h2 className="text-[28px] font-black text-[#F9004D] leading-none mb-1">{selectedItem?.id}</h2>
            </div>
            <button
              onClick={() => setIsPreviewOpen(false)}
              className="p-2 hover:bg-gray-200/50 rounded-full transition-colors"
            >
              <HiPlus className="text-2xl text-gray-400 transform rotate-45" />
            </button>
          </div>
        </div>

        {/* Content Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-2">
          <DataGroup title="Detalle General">
            <DataItem label="ID Reserva" value={selectedItem?.id} />
            <DataItem label="Fecha" value={format_date(selectedItem?.date)} />
            <DataItem label="Tienda" value={selectedItem?.sourceId} />
            <DataItem label="Estado" value={(_states as any)[selectedItem?.state] || selectedItem?.state} last />
          </DataGroup>

          {selectedItem?.items && Array.isArray(selectedItem.items) && selectedItem.items.length > 0 && (
            <DataGroup title="Artículos (Items)">
              {selectedItem.items.map((it: any, idx: number, arr: any[]) => (
                <div
                  key={idx}
                  className={`flex gap-4 items-center py-3 px-4 ${idx !== arr.length - 1 ? "border-b border-gray-100" : ""}`}
                >
                  <div className="flex flex-col flex-1">
                    <span className="text-[14px] font-bold text-gray-800">SKU: {it.sku}</span>
                    <span className="text-[12px] text-gray-500">Reason Code: {it.reasonCode}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[12px] font-medium text-gray-400 uppercase tracking-tight">Cant.</span>
                    <span className="text-[15px] font-bold text-[#007AFF]">{it.quantity}</span>
                  </div>
                </div>
              ))}
            </DataGroup>
          )}

          {selectedItem?.keys && Object.keys(selectedItem.keys).length > 0 && (
            <DataGroup title="Órdenes">
              {Object.entries(selectedItem.keys).map(([key, value], idx, arr) => (
                <DataItem key={key} label={`Órden #${Number(key) + 1}`} value={value} last={idx === arr.length - 1} />
              ))}
            </DataGroup>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 bg-white/50 backdrop-blur-md flex flex-row gap-3">
          <button
            onClick={() => setIsPreviewOpen(false)}
            className="flex-1 bg-white border border-gray-200 py-3 rounded-xl text-[14px] font-bold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 transition-all shadow-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  )

  const ViewToggle = () => (
    <div className="flex bg-gray-200/50 p-1 rounded-xl border border-gray-200 shadow-inner overflow-hidden">
      <button
        onClick={() => setViewMode("BLOCK")}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-bold transition-all duration-300",
          viewMode === "BLOCK" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700",
        )}
      >
        <HiViewGrid className="text-lg" />
        Bloques
      </button>
      <button
        onClick={() => setViewMode("TABLE")}
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-bold transition-all duration-300",
          viewMode === "TABLE" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700",
        )}
      >
        <HiViewList className="text-lg" />
        Tabla
      </button>
    </div>
  )

  const ListItemCard = ({ item }: { item: any }) => (
    <div className="w-full bg-white rounded-[12px] border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group mb-3 flex flex-col">
      <div className="flex flex-row justify-between items-center py-4 px-5 border-b border-gray-50 bg-gray-50/30 group-hover:bg-white transition-colors">
        <div className="flex flex-col">
          <span className="text-[13px] font-semibold text-gray-400 uppercase tracking-tight mb-0.5">ID Reserva</span>
          <span className="text-[17px] font-bold text-[#F9004D]">{item.id}</span>
        </div>
      </div>

      <div className="flex flex-col p-5 gap-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row justify-between items-start">
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-tight mb-1 text-right">
                Source
              </span>
              <span className="text-[13px] font-bold px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600">
                {item.sourceId || "[N/A]"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-between items-center pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span
              className={cn("text-[13px] font-medium", item.state === "OPEN" ? "text-green-500" : "text-gray-500")}
            >
              Estado: {(_states as any)[item.state] || item.state || "N/A"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Tooltip content="Vista rápida" align="top">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  handlePreview(item)
                }}
                className="w-9 h-9 flex items-center justify-center bg-white hover:bg-gray-50 rounded-full text-indigo-500 hover:text-indigo-600 transition-all border border-gray-200 shadow-sm hover:shadow-md"
              >
                <HiOutlineEye className="text-lg" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="w-full h-full overflow-auto flex flex-col items-center justify-start bg-[#F2F2F7]">
      <Breadcrumb data={[{ name: "Listado de reservas", href: "/reserves" }]} />
      <div className="w-full flex flex-col justify-center items-center py-4 px-6 mb-2">
        <TitleScreen title="Listado de reservas" />
      </div>

      <div className="w-full flex flex-col gap-6 px-6 pb-20">
        <div className="w-full flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4">
          <div className="flex-1 p-1">
            <SearchBarFilter
              handle_page_change={handle_page_change}
              classNameInput="!bg-white shadow-sm !border-[#ACA7A7] !font-medium !text-gray-700 focus:!border-gray-400 transition-all"
            />
          </div>
          <div className="flex flex-row items-center gap-3">
            <ViewToggle />
          </div>
        </div>

        <div className="w-full flex flex-col gap-4">
          <div className="flex flex-row justify-between items-center px-2">
            <h4 className="text-[13px] font-semibold text-gray-500 uppercase tracking-widest">
              {total_records} reservas encontradas
            </h4>
            <div className="p-1 scale-90 origin-right">
              <Paginator
                current_page={current_page}
                handle_page_change={handle_page_change}
                per_page={per_page}
                total_records={total_records}
                total_pages={total_pages}
                handle_per_page={handle_per_page}
              />
            </div>
          </div>

          <div className="w-full">
            {!loading_app && data_table_items?.length ? (
              viewMode === "BLOCK" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2">
                  {current_data.map((item: any, i: number) => (
                    <ListItemCard key={i} item={item} />
                  ))}
                </div>
              ) : (
                <div className="w-full bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-hidden">
                  <Table>
                    <Table.Head>
                      <Table.HeadCell className="!w-5 !bg-gray-50/50 !border-l-0">
                        <></>
                      </Table.HeadCell>
                      {columns.map((key: string) => (
                        <Table.HeadCell
                          key={key}
                          className="!bg-gray-50/50 !border-l-0 !text-[11px] !text-gray-400 !uppercase !tracking-wider"
                        >
                          {key}
                        </Table.HeadCell>
                      ))}
                    </Table.Head>
                    <Table.Body>
                      {data_table_items.map((item: any, i: number) => (
                        <Table.Row key={i}>
                          <Table.Cell className="flex flex-row items-center justify-end gap-2 !border-l-0 !bg-transparent group-hover:!bg-transparent transition-colors">
                            <Tooltip content="Vista rápida" align="top">
                              <button
                                onClick={() => handlePreview(current_data.find((raw: any) => raw.id === item.id))}
                              >
                                <HiOutlineEye className="text-[17px] text-indigo-500 cursor-pointer hover:scale-110 transition-transform" />
                              </button>
                            </Tooltip>
                          </Table.Cell>
                          {Object.values(item).map((value: any, j: number) => (
                            <Table.Cell
                              key={j}
                              className="!border-l-0 !bg-transparent !text-gray-600 group-hover:!bg-transparent transition-colors"
                            >
                              {value}
                            </Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              )
            ) : (
              <div className="w-full bg-white rounded-2xl p-12 text-center border border-dashed border-gray-300 shadow-inner">
                {loading_app ? (
                  <p className="text-gray-500 font-medium flex items-center justify-center gap-3">
                    <span className="h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                    Cargando reservas...
                  </p>
                ) : (
                  <p className="text-gray-400 italic">No se encontraron reservas que coincidan con la búsqueda</p>
                )}
              </div>
            )}
          </div>

          {data_table_items?.length > 0 && (
            <div className="flex justify-center mt-6">
              <Paginator
                current_page={current_page}
                handle_page_change={handle_page_change}
                per_page={per_page}
                total_records={total_records}
                total_pages={total_pages}
                handle_per_page={handle_per_page}
              />
            </div>
          )}
        </div>
      </div>
      <ItemPreviewModal />
    </div>
  )
}

export default ListScreen
